import styled, { css, keyframes } from 'styled-components';

/**
 * Le widget est un bundle autonome chargé sur des sites tiers : pas d'accès aux
 * variables CSS ni aux polices Google Fonts de l'app (index.css). Les valeurs ci-dessous
 * reprennent en dur l'esprit des tokens du design system (rayon, ombres, encre) sans
 * dépendance externe — police système uniquement (pas de requête réseau supplémentaire).
 */
const INK = '#0f172a';
const INK_MUTED = '#64748b';
const BORDER = '#eef1f6';
const SURFACE_MUTED = '#f6f7fb';
const RADIUS_LG = '22px';
const RADIUS_MD = '14px';
const SHADOW_PANEL = '0 20px 48px -12px rgb(15 23 42 / 0.22), 0 4px 16px -4px rgb(15 23 42 / 0.08)';
const SHADOW_FAB = '0 12px 24px -6px rgb(15 23 42 / 0.28), 0 4px 8px -2px rgb(15 23 42 / 0.12)';

const hideScrollbar = css`
  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const panelIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fabIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: scale(1); }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const bounceDot = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-3px); opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const SpinIcon = styled.span`
  display: inline-flex;
  animation: ${spin} 850ms linear infinite;
`;

export const Root = styled.div<{ $isLeft: boolean }>`
  position: fixed;
  bottom: 1.25rem;
  z-index: 999999;
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    Arial,
    'Noto Sans',
    'Liberation Sans',
    sans-serif;
  color: ${INK};
  ${(p) => (p.$isLeft ? 'left: 1.25rem;' : 'right: 1.25rem;')}
`;

export const Panel = styled.div<{ $isLeft: boolean }>`
  position: absolute;
  bottom: 80px;
  width: 25rem;
  max-width: calc(100vw - 40px);
  min-height: 280px;
  height: min(560px, calc(100vh - 6.5rem));
  background: #fff;
  border-radius: ${RADIUS_LG};
  box-shadow: ${SHADOW_PANEL};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom ${(p) => (p.$isLeft ? 'left' : 'right')};
  animation: ${panelIn} 220ms cubic-bezier(0.16, 1, 0.3, 1);
  ${(p) => (p.$isLeft ? 'left: 0;' : 'right: 0;')}

  @media (max-width: 480px), (max-height: 640px) {
    position: fixed;
    inset: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 100%;
    border-radius: 0;
  }
`;

export const Header = styled.div`
  position: relative;
  padding: 1.1rem 1.15rem;
  background: var(--crispclone-color, #2563eb);
  color: #fff;
  flex-shrink: 0;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(155deg, rgb(255 255 255 / 0.16), rgb(255 255 255 / 0) 55%);
    pointer-events: none;
  }
`;

export const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

export const Title = styled.span`
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
`;

export const CloseBtn = styled.button`
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.2);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 150ms ease, transform 150ms ease;

  &:hover {
    background: rgb(255 255 255 / 0.32);
    transform: rotate(90deg);
  }
`;

export const Subtitle = styled.p`
  position: relative;
  margin: 0.5rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: rgb(255 255 255 / 0.92);
`;

export const PresenceDot = styled.span<{ $online: boolean }>`
  position: relative;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: ${(p) => (p.$online ? '#4ade80' : 'rgb(255 255 255 / 0.5)')};
  flex-shrink: 0;

  ${(p) =>
    p.$online &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 999px;
        background: #4ade80;
        animation: ${pulseRing} 1.8s ease-out infinite;
      }
    `}
`;

export const Body = styled.div`
  position: relative;
  padding: 1.1rem;
  background: #fff;
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-direction: column;
`;

export const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${RADIUS_MD};
  background: rgb(255 255 255 / 0.96);
  border: 2px dashed var(--crispclone-color, #2563eb);
  color: var(--crispclone-color, #2563eb);
`;

export const DropIcon = styled.svg`
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.9;
`;

export const DropText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
`;

export const ErrorPara = styled.p`
  margin: 0 0 0.5rem;
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 500;
`;

export const LoadingWrap = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const LoadingDot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #cbd5e1;
  animation: ${bounceDot} 1.1s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}ms;
`;

export const LeadForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
`;

export const LeadInput = styled.input`
  width: 100%;
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS_MD};
  padding: 0.7rem 0.85rem;
  font-size: 0.85rem;
  outline: none;
  background: ${SURFACE_MUTED};
  transition: border-color 150ms ease, background 150ms ease;

  &:focus {
    border-color: var(--crispclone-color, #2563eb);
    background: #fff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const Messages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  flex: 1;
  min-height: 140px;
  margin-bottom: 0.85rem;
  overflow-y: auto;
  overflow-x: hidden;
  ${hideScrollbar}
`;

export const MessageGroup = styled.div<{ $isVisitor: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  ${(p) => p.$isVisitor && 'flex-direction: row-reverse;'}
`;

export const SenderAvatar = styled.div<{ $bg?: string | null }>`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$bg ?? '#e2e8f0'};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  margin-bottom: 2px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AvatarSpacer = styled.div`
  width: 24px;
  flex-shrink: 0;
`;

export const BubbleColumn = styled.div<{ $isVisitor: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-width: 78%;
  align-items: ${(p) => (p.$isVisitor ? 'flex-end' : 'flex-start')};
`;

export const SenderName = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${INK_MUTED};
  padding: 0 0.2rem;
`;

export const Bubble = styled.div<{ $isVisitor: boolean; $imageOnly: boolean; $grouped: boolean }>`
  padding: 0.65rem 0.85rem;
  border-radius: ${RADIUS_MD};
  font-size: 0.87rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
  width: fit-content;
  max-width: 100%;

  ${(p) =>
    p.$imageOnly
      ? css`
          background: transparent;
          border: none;
          padding: 0;
          color: ${INK};
        `
      : p.$isVisitor
        ? css`
            background: var(--crispclone-color, #2563eb);
            color: #fff;
            border-bottom-right-radius: ${p.$grouped ? RADIUS_MD : '4px'};
          `
        : css`
            background: ${SURFACE_MUTED};
            color: ${INK};
            border-bottom-left-radius: ${p.$grouped ? RADIUS_MD : '4px'};
          `}
`;

export const WelcomeCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.85rem 0.95rem;
  border-radius: ${RADIUS_MD};
  background: ${SURFACE_MUTED};
  color: ${INK};
  margin-bottom: 0.2rem;
`;

export const WelcomeIcon = styled.span`
  font-size: 1.1rem;
  line-height: 1;
  flex-shrink: 0;
`;

export const WelcomeText = styled.span`
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.87rem;
  line-height: 1.45;
`;

export const MsgContent = styled.span`
  display: block;
  word-break: break-word;

  p {
    margin: 0;
  }

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  code {
    background: rgb(0 0 0 / 0.08);
    padding: 0.1rem 0.3rem;
    border-radius: 0.3rem;
    font-size: 0.85em;
  }

  blockquote {
    margin: 0;
    padding-left: 0.5rem;
    border-left: 2px solid #cbd5e1;
    font-style: italic;
  }

  ul,
  ol {
    margin: 0.25rem 0 0;
    padding-left: 1.25rem;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: 240px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: ${RADIUS_MD};
    cursor: pointer;
  }

  .message-attachment-pdf {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border-radius: ${RADIUS_MD};
    background: rgb(255 255 255 / 0.95);
    border: 1px solid rgb(226 232 240 / 0.8);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
    margin-top: 0.375rem;
  }

  .message-attachment-pdf a {
    text-decoration: none;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: ${INK};
  }

  .message-attachment-pdf a:hover {
    color: #020617;
  }
`;

export const TimeSpan = styled.span<{ $onPrimary: boolean }>`
  display: block;
  margin-top: 0.3rem;
  font-size: 0.68rem;
  font-weight: 500;
  color: ${(p) => (p.$onPrimary ? 'rgb(255 255 255 / 0.85)' : '#94a3b8')};
`;

const msgFlash = keyframes`
  0% { box-shadow: 0 0 0 3px rgb(37 99 235 / 0.35); }
  100% { box-shadow: 0 0 0 0 rgb(37 99 235 / 0); }
`;

export const SwipeWrap = styled.div<{ $swipeX: number; $swiping: boolean; $highlighted: boolean }>`
  position: relative;
  transform: ${(p) => (p.$swipeX ? `translateX(${p.$swipeX}px)` : 'none')};
  transition: ${(p) => (p.$swiping ? 'none' : 'transform 0.2s ease')};
  border-radius: ${RADIUS_MD};
  ${(p) =>
    p.$highlighted &&
    css`
      > * {
        animation: ${msgFlash} 1.5s ease;
      }
    `}

  &:hover .reply-trigger-btn,
  &:hover .edit-trigger-btn {
    opacity: 1;
  }
`;

export const SwipeReplyIcon = styled.span<{ $isVisitor: boolean; $opacity: number }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$isVisitor ? 'right: -1.5rem;' : 'left: -1.5rem;')}
  color: var(--crispclone-color, #2563eb);
  opacity: ${(p) => p.$opacity};
  display: flex;
`;

export const ReplyTriggerBtn = styled.button`
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid ${BORDER};
  background: #fff;
  color: ${INK_MUTED};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
  box-shadow: 0 2px 6px rgb(0 0 0 / 0.08);
`;

export const EditTriggerBtn = styled.button<{ $alwaysVisible: boolean }>`
  position: absolute;
  top: -0.4rem;
  right: 1.6rem;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid ${BORDER};
  background: #fff;
  color: ${INK_MUTED};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(p) => (p.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s;
  box-shadow: 0 2px 6px rgb(0 0 0 / 0.08);
`;

export const EditedTag = styled.span`
  margin-left: 0.25rem;
`;

export const EditComposerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 200px;
`;

export const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 2.2rem;
  max-height: 120px;
  resize: none;
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS_MD};
  background: #fff;
  color: ${INK};
  outline: none;
  font-size: 0.87rem;
  line-height: 1.4;
  padding: 0.4rem 0.55rem;
  font-family: inherit;

  &:focus {
    border-color: var(--crispclone-color, #2563eb);
  }
`;

export const EditActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
`;

export const EditCancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 8px;
  border: none;
  padding: 0.3rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  background: ${SURFACE_MUTED};
  color: ${INK_MUTED};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const EditSaveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 8px;
  border: none;
  padding: 0.3rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  background: var(--crispclone-color, #2563eb);
  color: #fff;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuotedPreview = styled.button`
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  text-align: left;
  border: none;
  border-left: 3px solid rgb(255 255 255 / 0.6);
  background: rgb(0 0 0 / 0.06);
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  margin-bottom: 0.3rem;
  cursor: pointer;
  max-width: 100%;
`;

export const QuotedSender = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
`;

export const QuotedContent = styled.span`
  font-size: 0.75rem;
  opacity: 0.85;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ReplyPreviewBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  margin-bottom: 0.4rem;
  border-radius: ${RADIUS_MD};
  background: ${SURFACE_MUTED};
`;

export const ReplyPreviewText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
  border-left: 3px solid var(--crispclone-color, #2563eb);
  padding-left: 0.5rem;
`;

export const ReplyPreviewSender = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${INK};
`;

export const ReplyPreviewSnippet = styled.span`
  font-size: 0.78rem;
  color: ${INK_MUTED};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ReplyPreviewClose = styled.button`
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;

  &:hover {
    color: ${INK};
  }
`;

export const TypingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.7rem 0.9rem;
  border-radius: ${RADIUS_MD};
  border-bottom-left-radius: 4px;
  background: ${SURFACE_MUTED};
  width: fit-content;
`;

export const TypingDot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #94a3b8;
  animation: ${bounceDot} 1.1s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}ms;
`;

export const ComposerForm = styled.form`
  flex-shrink: 0;
`;

export const Composer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  padding: 0.45rem 0.5rem 0.45rem 0.75rem;
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS_LG};
  background: #fff;
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:focus-within {
    border-color: var(--crispclone-color, #2563eb);
    box-shadow: 0 0 0 3px rgb(37 99 235 / 0.15);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--crispclone-color, #2563eb) 15%, transparent);
  }
`;

export const AttachWrap = styled.div`
  display: flex;
  align-items: center;
`;

export const AttachLabel = styled.label`
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
  flex-shrink: 0;

  &:hover {
    background: ${SURFACE_MUTED};
    color: ${INK_MUTED};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const MessageTextarea = styled.textarea`
  flex: 1;
  min-width: 0;
  min-height: 40px;
  max-height: 120px;
  resize: none;
  border: 0;
  background: transparent;
  outline: none;
  font-size: 0.87rem;
  line-height: 1.5rem;
  padding: 0.55rem 0.25rem;
  overflow-y: auto;
  font-family: inherit;
  ${hideScrollbar}

  &::placeholder {
    color: #94a3b8;
  }
`;

export const AttachmentPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0 0.6rem;
  height: 2.1rem;
  border-radius: 999px;
  border: 0;
  background: ${SURFACE_MUTED};
  color: ${INK_MUTED};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: #eef1f6;
  }
`;

export const AttachmentBadgePdf = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 0.35rem;
  background: #fee2e2;
  color: #dc2626;
  font-weight: 700;
  font-size: 9px;
`;

export const AttachmentThumb = styled.span`
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 0.35rem;
  background: #e2e8f0;
  overflow: hidden;
  flex-shrink: 0;
`;

export const AttachmentThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const AttachmentName = styled.span`
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AttachmentRemove = styled.span`
  font-size: 12px;
  color: #94a3b8;
  display: inline-flex;
`;

export const SendBtn = styled.button`
  flex-shrink: 0;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: var(--crispclone-color, #2563eb);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease, opacity 150ms ease;

  &:hover:not(:disabled) {
    transform: scale(1.06);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ModalOverlay = styled.div<{ $padded?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1000000;
  background: rgb(15 23 42 / 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => p.$padded && 'padding: 0.75rem;'}
`;

export const ModalContentImageWrap = styled.div`
  position: relative;
  display: inline-block;
  max-width: 90vw;
  max-height: 90vh;
`;

export const ImageModalClose = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  color: #fff;
  background: rgb(0 0 0 / 0.55);
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.35);
  transform: translate(35%, -35%);
  transition: background 150ms ease;

  &:hover {
    background: rgb(0 0 0 / 0.75);
  }
`;

export const ImageModalCloseIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
`;

export const ModalImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  border-radius: ${RADIUS_MD};
  box-shadow: ${SHADOW_PANEL};
  display: block;
`;

export const PdfModal = styled.div`
  width: 100%;
  max-width: 48rem;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: ${RADIUS_MD};
  box-shadow: ${SHADOW_PANEL};
  overflow: hidden;
`;

export const PdfHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid ${BORDER};
  background: ${SURFACE_MUTED};
  flex-shrink: 0;
`;

export const PdfTitle = styled.span`
  font-size: 0.87rem;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PdfCloseBtn = styled.button`
  border: 0;
  background: transparent;
  padding: 0.375rem;
  border-radius: 0.5rem;
  cursor: pointer;
  color: ${INK_MUTED};
  display: inline-flex;

  &:hover {
    background: #e2e8f0;
    color: #334155;
  }
`;

export const PdfCloseIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
`;

export const PdfFrame = styled.iframe`
  width: 100%;
  flex: 1;
  min-height: 60vh;
  border: 0;
`;

export const FabWrap = styled.div`
  position: relative;
`;

export const Fab = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 999px;
  border: 0;
  box-shadow: ${SHADOW_FAB};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--crispclone-color, #2563eb);
  transition: transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 150ms ease;
  animation: ${fabIn} 260ms cubic-bezier(0.34, 1.56, 0.64, 1);

  &:hover {
    transform: scale(1.06);
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const UnreadBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgb(239 68 68 / 0.4);
`;
